import { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  Divider,
  Chip,
  alpha,
  Fade,
  Grow,
  Zoom,
  IconButton,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  LocalShipping as TruckIcon,
} from "@mui/icons-material";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitStatus, setSubmitStatus] = useState(null);

  // Brand colors
  const brandColors = {
    green: '#a6ce39',
    black: '#050606',
    orange: '#ec7723',
    white: '#ffffff'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    console.log("Form submitted:", formData);
    setSubmitStatus("success");
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });

    // Reset status after 5 seconds
    setTimeout(() => {
      setSubmitStatus(null);
    }, 5000);
  };

  const contactInfo = [
    {
      icon: <PhoneIcon sx={{ fontSize: 32 }} />,
      title: "Call Us",
      details: [
        { label: "Director", value: "+268 7602 1597" },
        { label: "Office", value: "+268 2518 5080" },
        { label: "Office Admin", value: "+268 7812 2941" },
      ],
      color: brandColors.orange,
    },
    {
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      title: "Email Us",
      details: [
        { label: "Primary Email", value: "transcologistics26@gmail.com" },
      ],
      color: brandColors.green,
    },
    {
      icon: <LocationIcon sx={{ fontSize: 32 }} />,
      title: "Visit Us",
      details: [
        { label: "Physical Address", value: "Nsulutane MR33 Road towards MK111 International Airport" },
        { label: "Postal Address", value: "P.O Box C2395, Hub Manzini, Eswatini" },
      ],
      color: brandColors.orange,
    },
    {
      icon: <TimeIcon sx={{ fontSize: 32 }} />,
      title: "Business Hours",
      details: [
        { label: "Monday - Friday", value: "8:00 AM - 5:00 PM" },
        { label: "Saturday", value: "8:00 AM - 1:00 PM" },
        { label: "Sunday", value: "Closed" },
      ],
      color: brandColors.green,
    },
  ];

  const staffContacts = [
    { name: "Mr Mfanawenkosi Sifundza", role: "Director", phone: "+268 7602 1597" },
    { name: "Miss Mayibongwe Magongo", role: "Risk Manager", phone: "+268 7677 3299" },
    { name: "Mr Sithembiso Ginindza", role: "Operations Manager", phone: "+268 7602 2974" },
    { name: "Miss Bonisile Matsebula", role: "Office Admin", phone: "+268 7812 2941" },
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, label: "Facebook", href: "#", color: "#1877F2" },
    { icon: <TwitterIcon />, label: "Twitter", href: "#", color: "#1DA1F2" },
    { icon: <LinkedInIcon />, label: "LinkedIn", href: "#", color: "#0077B5" },
    { icon: <WhatsAppIcon />, label: "WhatsApp", href: "#", color: "#25D366" },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          bgcolor: brandColors.black,
          color: brandColors.white,
          py: { xs: 10, md: 15 },
          textAlign: "center",
          overflow: "hidden",
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
          },
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, ${alpha(brandColors.orange, 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, ${alpha(brandColors.green, 0.05)} 0%, transparent 50%)
            `,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box>
              <Chip
                label="GET IN TOUCH"
                icon={<PhoneIcon />}
                sx={{
                  mb: 4,
                  backgroundColor: alpha(brandColors.green, 0.2),
                  color: brandColors.green,
                  border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                  px: 3,
                  py: 1,
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  backdropFilter: "blur(10px)",
                  '& .MuiChip-icon': {
                    color: brandColors.green,
                  }
                }}
              />

              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  fontSize: { xs: "2.8rem", md: "4rem" },
                  background: `linear-gradient(135deg, ${brandColors.white} 0%, ${brandColors.green} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                Contact TRANSCO LOGISTICS
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  mb: 4,
                  color: brandColors.orange,
                  fontWeight: 700,
                  fontStyle: 'italic',
                }}
              >
                "Steering You Towards Success"
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  maxWidth: 800,
                  mx: "auto",
                  mb: 6,
                  color: alpha(brandColors.white, 0.9),
                  lineHeight: 1.8,
                  fontWeight: 400,
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                }}
              >
                Have questions about our services? Need a custom quote? Our team is here to help you
                with all your logistics needs. Get in touch today!
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="tel:+26876021597"
                  startIcon={<PhoneIcon />}
                  sx={{
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.8, md: 2.2 },
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    borderRadius: "14px",
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                    boxShadow: `0 8px 32px ${alpha(brandColors.orange, 0.4)}`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 40px ${alpha(brandColors.orange, 0.5)}`,
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Call Now
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href="mailto:transcologistics26@gmail.com"
                  startIcon={<EmailIcon />}
                  sx={{
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.8, md: 2.2 },
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    borderWidth: 2,
                    color: brandColors.white,
                    borderColor: alpha(brandColors.white, 0.3),
                    borderRadius: "14px",
                    fontWeight: 700,
                    backgroundColor: alpha(brandColors.black, 0.3),
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      borderColor: brandColors.white,
                      backgroundColor: alpha(brandColors.white, 0.1),
                      transform: "translateY(-4px)",
                      backdropFilter: "blur(20px)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Send Email
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 10, md: 15 }, position: "relative" }}>
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 10% 20%, ${alpha(brandColors.green, 0.03)} 0%, transparent 50%)`,
            zIndex: -1,
          }}
        />

        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <Fade in timeout={600}>
              <Box>
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 900,
                    color: brandColors.black,
                    mb: 3,
                  }}
                >
                  Our Contact Information
                </Typography>
                
                <Typography
                  variant="h6"
                  color="text.secondary"
                  paragraph
                  sx={{ mb: 4, lineHeight: 1.8, fontWeight: 400 }}
                >
                  Get in touch with our team of logistics experts. We're here to help you with
                  transportation solutions, quotes, and any questions you may have.
                </Typography>

                <Stack spacing={4} mt={4}>
                  {contactInfo.map((info, index) => (
                    <Grow in timeout={(index + 1) * 200} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "16px",
                          border: `1px solid ${alpha(info.color, 0.2)}`,
                          background: `linear-gradient(135deg, ${alpha(info.color, 0.05)} 0%, transparent 100%)`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: info.color,
                            transform: "translateY(-4px)",
                            boxShadow: `0 12px 32px ${alpha(info.color, 0.1)}`,
                          },
                        }}
                      >
                        <Box display="flex" gap={3} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "14px",
                              background: alpha(info.color, 0.1),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: info.color,
                              flexShrink: 0,
                            }}
                          >
                            {info.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" gutterBottom fontWeight={800} color={brandColors.black}>
                              {info.title}
                            </Typography>
                            {info.details.map((detail, idx) => (
                              <Box key={idx} sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                                  {detail.label}
                                </Typography>
                                <Typography variant="body1" fontWeight={700} color={brandColors.black}>
                                  {detail.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Paper>
                    </Grow>
                  ))}
                </Stack>

                <Divider sx={{ my: 6, borderColor: alpha(brandColors.black, 0.1) }} />

                {/* Key Staff Contacts */}
                <Typography variant="h4" gutterBottom fontWeight={900} sx={{ color: brandColors.black, mb: 4 }}>
                  Key Staff Contacts
                </Typography>
                
                <Grid container spacing={2}>
                  {staffContacts.map((staff, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Grow in timeout={(index + 1) * 200}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: "14px",
                            border: `1px solid ${alpha(brandColors.orange, 0.2)}`,
                            background: alpha(brandColors.orange, 0.03),
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: brandColors.orange,
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={800} color={brandColors.black} gutterBottom>
                            {staff.role}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                            {staff.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: brandColors.green }} />
                            <Typography variant="body2" fontWeight={700} color={brandColors.black}>
                              {staff.phone}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grow>
                    </Grid>
                  ))}
                </Grid>

                {/* Social Media */}
                <Box sx={{ mt: 6 }}>
                  <Typography variant="h6" gutterBottom fontWeight={800} sx={{ color: brandColors.black, mb: 3 }}>
                    Connect With Us
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {socialLinks.map((social, index) => (
                      <IconButton
                        key={index}
                        component="a"
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: brandColors.white,
                          bgcolor: social.color,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            bgcolor: alpha(social.color, 0.8),
                            transform: "translateY(-3px)",
                            boxShadow: `0 6px 16px ${alpha(social.color, 0.4)}`,
                          },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Zoom in timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: "24px",
                  border: `2px solid ${brandColors.orange}`,
                  background: `linear-gradient(135deg, ${alpha(brandColors.white, 0.95)} 0%, ${alpha(brandColors.white, 0.98)} 100%)`,
                  boxShadow: `0 20px 40px ${alpha(brandColors.orange, 0.1)}`,
                  position: "relative",
                  overflow: "hidden",
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
                  }
                }}
              >
                <Typography variant="h3" gutterBottom fontWeight={900} sx={{ color: brandColors.black, mb: 2 }}>
                  Send Us a Message
                </Typography>
                
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontWeight: 400 }}>
                  Fill out the form below and we'll get back to you within 24 hours.
                </Typography>

                {submitStatus === "success" && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: "12px",
                      background: alpha(brandColors.green, 0.1),
                      color: brandColors.green,
                      border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                    }}
                    icon={<CheckIcon sx={{ color: brandColors.green }} />}
                  >
                    Thank you for your message! We'll get back to you within 24 hours.
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: "12px",
                            '&:hover fieldset': {
                              borderColor: brandColors.orange,
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: "12px",
                            '&:hover fieldset': {
                              borderColor: brandColors.orange,
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: "12px",
                            '&:hover fieldset': {
                              borderColor: brandColors.green,
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: "12px",
                            '&:hover fieldset': {
                              borderColor: brandColors.orange,
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        variant="outlined"
                        multiline
                        rows={6}
                        placeholder="Tell us about your logistics needs, route requirements, or any specific requirements you have..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: "16px",
                            '&:hover fieldset': {
                              borderColor: brandColors.green,
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        endIcon={<SendIcon />}
                        sx={{
                          py: 2,
                          borderRadius: "14px",
                          fontWeight: 800,
                          fontSize: "1rem",
                          background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                          boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.3)}`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                            transform: "translateY(-3px)",
                            boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.4)}`,
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                {/* Quick Response Guarantee */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: "16px",
                    background: alpha(brandColors.green, 0.05),
                    border: `1px solid ${alpha(brandColors.green, 0.2)}`,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <CheckIcon sx={{ color: brandColors.green, fontSize: 24 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800} color={brandColors.black}>
                        24-Hour Response Guarantee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We guarantee to respond to all inquiries within 24 business hours
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>

        {/* Map/Address Section */}
        <Fade in timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              mt: { xs: 10, md: 15 },
              p: { xs: 4, md: 5 },
              borderRadius: "24px",
              border: `1px solid ${alpha(brandColors.black, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(brandColors.white, 0.95)} 0%, ${alpha(brandColors.white, 0.98)} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
              }}
            />

            <Typography variant="h2" gutterBottom fontWeight={900} sx={{ color: brandColors.black, mb: 3 }}>
              Our Location
            </Typography>
            
            <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontWeight: 400 }}>
              Visit us at our headquarters in Eswatini
            </Typography>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "20px",
                    background: alpha(brandColors.orange, 0.05),
                    border: `1px solid ${alpha(brandColors.orange, 0.2)}`,
                    textAlign: "left",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={3}>
                    <LocationIcon sx={{ fontSize: 40, color: brandColors.orange, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" gutterBottom fontWeight={800} sx={{ color: brandColors.black, mb: 2 }}>
                        Physical Address
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                        Nsulutane MR33 Road towards MK111 International Airport
                      </Typography>
                      
                      <Typography variant="h6" gutterBottom fontWeight={800} sx={{ color: brandColors.black, mb: 2, mt: 3 }}>
                        Postal Address
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        P.O Box C2395, Hub Manzini, Eswatini
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<LocationIcon />}
                    sx={{
                      mt: 4,
                      px: 4,
                      py: 1.5,
                      borderRadius: "14px",
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${brandColors.green} 0%, ${alpha(brandColors.green, 0.9)} 100%)`,
                      boxShadow: `0 6px 20px ${alpha(brandColors.green, 0.3)}`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${brandColors.green} 0%, ${brandColors.green} 100%)`,
                        transform: "translateY(-3px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Get Directions
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: 300,
                    bgcolor: alpha(brandColors.black, 0.05),
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px dashed ${alpha(brandColors.orange, 0.3)}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${alpha(brandColors.orange, 0.1)} 0%, ${alpha(brandColors.green, 0.05)} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <TruckIcon sx={{ fontSize: 60, color: alpha(brandColors.orange, 0.3), mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ textAlign: "center" }}>
                      Map Integration Available
                      <Typography variant="body2" display="block" sx={{ mt: 1 }}>
                        Google Maps can be integrated here
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      </Container>
    </>
  );
};

export default Contact;